
import React, { useRef, useEffect, ReactNode, Children } from 'react';
import { motion, useInView, useAnimationControls, TargetAndTransition } from 'framer-motion';

interface FadeInProps {
  children: ReactNode;
  direction?: 'up' | 'down' | 'left' | 'right';
  delay?: number;
  duration?: number;
  className?: string;
  style?: React.CSSProperties;
  once?: boolean;
}

export const FadeIn = ({
  children,
  direction = "up",
  delay = 0,
  duration = 0.5,
  className = "",
  style = {},
  once = true,
}: FadeInProps) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once });
  
  const getDirectionVariants = () => {
    switch (direction) {
      case "up":
        return { y: 40 };
      case "down":
        return { y: -40 };
      case "left":
        return { x: 40 };
      case "right":
        return { x: -40 };
      default:
        return { y: 0 };
    }
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, ...getDirectionVariants() }}
      animate={isInView ? { opacity: 1, y: 0, x: 0 } : { opacity: 0, ...getDirectionVariants() }}
      transition={{ duration, delay, ease: "easeOut" }}
      className={className}
      style={style}
    >
      {children}
    </motion.div>
  );
};

interface StaggerContainerProps {
  children: ReactNode;
  delay?: number;
  staggerDelay?: number;
  className?: string;
  animation?: TargetAndTransition;
}

export const StaggerContainer = ({
  children,
  delay = 0,
  staggerDelay = 0.1,
  className = "",
  animation = {
    opacity: 1,
    y: 0,
  },
}: StaggerContainerProps) => {
  const controls = useAnimationControls();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  
  useEffect(() => {
    if (isInView) {
      controls.start((i) => ({
        ...animation,
        transition: { delay: delay + i * staggerDelay },
      }));
    }
  }, [isInView, controls, delay, staggerDelay, animation]);
  
  return (
    <motion.div ref={ref} className={className}>
      {Children.map(children, (child, i) => {
        // Check if child is valid before rendering
        if (React.isValidElement(child)) {
          const childType = child.type;
          if (childType && typeof childType !== 'string') {
            return (
              <motion.div
                key={i}
                custom={i}
                initial={{ opacity: 0, y: 20 }}
                animate={controls}
              >
                {child}
              </motion.div>
            );
          }
        }
        return child; // Return the child as is if it doesn't meet our conditions
      })}
    </motion.div>
  );
};

export const GradientBackground = ({ children }: { children: ReactNode }) => {
  return (
    <div className="relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-background/20 via-background to-background blur-3xl"></div>
      <div className="relative z-10">{children}</div>
    </div>
  );
};
